import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';

export type LoaderProps = {
  message?: string;
  visible?: boolean;
  /** Compact spinner for buttons or inline rows (no card chrome). */
  inline?: boolean;
  /** Fixed overlay over the whole viewport (portaled so transformed dialogs don't clip it). */
  fullPage?: boolean;
};

function Loader({ message = 'Loading...', visible = true, inline = false, fullPage = false }: LoaderProps) {
  const [show, setShow] = useState(visible);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (visible) {
      setShow(true);
    } else {
      // Delay unmount for fade out
      timeout = setTimeout(() => setShow(false), 300);
    }
    return () => clearTimeout(timeout);
  }, [visible]);

  if (inline) {
    return (
      <span
        style={{
          display: show && visible ? 'inline-flex' : 'none',
          alignItems: 'center',
          verticalAlign: 'middle',
        }}
      >
        <Image
          src="/loader.gif"
          alt=""
          width={18}
          height={18}
          style={{ objectFit: 'contain', display: 'block' }}
        />
        {message ? (
          <span style={{ marginLeft: 6, fontSize: 12, color: '#153C7F' }}>{message}</span>
        ) : null}
      </span>
    );
  }

  const card = (
    <div
      style={{
        display: show ? 'flex' : 'none',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Poppins, sans-serif',
        background: '#F3F8FF',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(21, 60, 127, 0.08)',
        padding: 32,
        minWidth: 240,
        minHeight: 240,
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.98)',
        transition: 'opacity 0.3s cubic-bezier(.4,0,.2,1), transform 0.3s cubic-bezier(.4,0,.2,1)',
      }}
    >
      <Image src="/loader.gif" alt="Loading..." width={120} height={120} style={{ objectFit: 'contain', display: 'block' }} />
      <div style={{ color: '#153C7F', fontWeight: 600, marginTop: 16, fontSize: 18 }}>{message}</div>
    </div>
  );

  if (!fullPage) return card;
  if (!mounted || !show) return null;

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(2px)',
        zIndex: 15000,
      }}
    >
      {card}
    </div>,
    document.body,
  );
}

export default Loader;
